import { Injectable } from '@angular/core';
import { fromGroupchatType, GROUPCHAT_TYPE_PREFIX, isSystemKeyValType, toGroupchatType } from '@shared/ProjectKeyValStore';
import { ProjectKeyvalAdminService } from './project-keyval-admin.service';
import { ProjectService } from './project.service';

export const DEFAULT_GROUPCHAT_CHANNEL = 'Gruppenchat';

export { GROUPCHAT_TYPE_PREFIX, toGroupchatType, fromGroupchatType, isSystemKeyValType };

@Injectable({ providedIn: 'root' })
export class GroupchatChannelRegistryService {
  private _channels = new Set<string>();

  constructor(
    private projectKeyvalService: ProjectKeyvalAdminService,
    private projectService: ProjectService
  ) { }

  private get _storageKey(): string {
    const projectId = this.projectService.selectedProject?._id ?? '-';
    return `groupchat_channels_${projectId}`;
  }

  private _loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this._storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as string[];
        if (Array.isArray(parsed)) {
          for (const ch of parsed) {
            if (typeof ch === 'string' && ch.trim()) {
              this._channels.add(ch.trim());
            }
          }
        }
      }
    } catch { /* noop */ }
  }

  private _saveToStorage(): void {
    try {
      localStorage.setItem(this._storageKey, JSON.stringify(Array.from(this._channels)));
    } catch { /* noop */ }
  }

  public async refresh(): Promise<void> {
    const projectId = this.projectService.selectedProject?._id ?? '-';
    try {
      const keyvals = await this.projectKeyvalService.getProjectKeyVals(projectId);
      this._channels.clear();
      for (const kv of keyvals) {
        if (typeof kv.key === 'string' && kv.key.startsWith('msg') && kv.type.startsWith(GROUPCHAT_TYPE_PREFIX)) {
          this._channels.add(fromGroupchatType(kv.type));
        }
      }
    } catch { /* noop */ }
    this._loadFromStorage();
    this._channels.add(DEFAULT_GROUPCHAT_CHANNEL);
  }

  public getChannels(): string[] {
    const channels = Array.from(this._channels);
    // Default-Kanal immer an erster Stelle anzeigen, damit er als Vorauswahl im Dropdown erscheint.
    channels.sort((a, b) => (a === DEFAULT_GROUPCHAT_CHANNEL ? -1 : b === DEFAULT_GROUPCHAT_CHANNEL ? 1 : 0));
    return channels;
  }

  public addChannel(name: string): void {
    const trimmed = name.trim();
    if (trimmed) {
      this._channels.add(trimmed);
      this._saveToStorage();
    }
  }

  public removeChannel(name: string): void {
    this._channels.delete(name);
    this._saveToStorage();
  }

  public getDropdownOptions(): [string, string][] {
    return this.getChannels().map(ch => [ch, ch] as [string, string]);
  }
}
